/**
 * web-account-pages D1 — the signed-in client's account reads on the website
 * session: the dashboard (home and Bookings' upcoming), the paged past
 * bookings, the profile with its save, the saved cards with Remove, and a
 * booking's cancel. The Mini App's own routes through `webFetch`; every query
 * sits under `CLIENT_SCOPE`, so a sign-out or another person drops it. A
 * save's answer belongs to who sent it: one that lands after a sign-out or
 * another person's sign-in is dropped, not written into their reads (Codex
 * review 2026-10-26).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DASHBOARD_QUERY } from '@/lib/client/queryFreshness';
import type { BookingDetail } from '@/types/bookingDetail';
import type { BookingHubDashboard, PastBookingsPage } from '@/types/dashboard';
import type { PaymentMethodsPayload, ProfilePayload, ProfileUpdateInput } from '@/types/profile';

import { CLIENT_SCOPE, signedInId, useWebSession, webFetch } from '../api/session';

const PAST_PAGE_SIZE = 15;

export const accountKeys = {
  dashboard: [...CLIENT_SCOPE, 'dashboard'] as const,
  past: [...CLIENT_SCOPE, 'past'] as const,
  profile: [...CLIENT_SCOPE, 'profile'] as const,
  cards: [...CLIENT_SCOPE, 'payment-methods'] as const,
  booking: (id: string) => [...CLIENT_SCOPE, 'booking', id] as const,
};

function useSignedIn(): boolean {
  return useWebSession().status === 'signedIn';
}

/** Who sent a save, taken as it starts; its answer is applied only while that person is still signed in. */
interface Sender {
  readonly who: string | null;
}

/**
 * The dashboard read (`GET /booking-hub`): next booking, upcoming, waiting forms, the unread count. `enabled: false`
 * holds the read, for a piece that needs it only in some states; the reader stays mounted either way. `staleTime` lets
 * a reader that turns on after the page's first read take that read instead of making another.
 */
export function useWebDashboard({ enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {}) {
  const signedIn = useSignedIn();
  return useQuery({
    queryKey: accountKeys.dashboard,
    queryFn: () => webFetch<BookingHubDashboard>('/booking-hub'),
    enabled: signedIn && enabled,
    ...DASHBOARD_QUERY,
    ...(staleTime === undefined ? {} : { staleTime }),
  });
}

/** Past and cancelled bookings, newest first, a page at a time (`GET /booking-hub/past`); `enabled: false` holds the read. */
export function useWebPastBookings({ enabled = true }: { enabled?: boolean } = {}) {
  const signedIn = useSignedIn();
  return useInfiniteQuery({
    queryKey: accountKeys.past,
    queryFn: ({ pageParam }) =>
      webFetch<PastBookingsPage>(`/booking-hub/past?limit=${PAST_PAGE_SIZE}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: signedIn && enabled,
    retry: false,
  });
}

/** Cancel or withdraw the client's own booking; the booking's read takes the answer, the lists re-read. */
export function useWebCancelBooking(bookingId: string) {
  const queryClient = useQueryClient();
  return useMutation<BookingDetail, Error, void, Sender>({
    retry: false,
    mutationFn: async () =>
      (await webFetch<{ booking: BookingDetail }>(`/booking-hub/booking/${encodeURIComponent(bookingId)}/cancel`, { method: 'POST' })).booking,
    onMutate: () => ({ who: signedInId(queryClient) }),
    onSuccess: (booking, _v, sent) => {
      if (sent?.who !== signedInId(queryClient)) return;
      queryClient.setQueryData(accountKeys.booking(bookingId), booking);
      void queryClient.invalidateQueries({ queryKey: accountKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: accountKeys.past });
    },
    onError: (_e, _v, sent) => {
      if (sent?.who !== signedInId(queryClient)) return;
      // A 409: the booking changed under the page (the studio acted first); show its true state.
      void queryClient.invalidateQueries({ queryKey: accountKeys.booking(bookingId) });
    },
  });
}

/** Profile: identity and My info (`GET /profile`). */
export function useWebProfile() {
  const signedIn = useSignedIn();
  return useQuery({
    queryKey: accountKeys.profile,
    queryFn: () => webFetch<ProfilePayload>('/profile'),
    enabled: signedIn,
    retry: false,
  });
}

/** Saves the four My info fields together (`PATCH /profile`); the page takes the answer as is. */
export function useWebUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation<ProfilePayload, Error, ProfileUpdateInput, Sender>({
    retry: false,
    mutationFn: (input) => webFetch<ProfilePayload>('/profile', { method: 'PATCH', body: JSON.stringify(input) }),
    onMutate: () => ({ who: signedInId(queryClient) }),
    onSuccess: (payload, _input, sent) => {
      if (sent?.who !== signedInId(queryClient)) return;
      queryClient.setQueryData(accountKeys.profile, payload);
      // The home's welcome and the header's initials read the name too.
      void queryClient.invalidateQueries({ queryKey: accountKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: ['web', 'session'] });
    },
  });
}

/** Saved cards (`GET /profile/payment-methods`): its own read, so a card outage leaves the rest of Profile. */
export function useWebPaymentMethods() {
  const signedIn = useSignedIn();
  return useQuery({
    queryKey: accountKeys.cards,
    queryFn: () => webFetch<PaymentMethodsPayload>('/profile/payment-methods'),
    enabled: signedIn,
    retry: false,
  });
}

/** Removes a saved card, then re-reads the list. */
export function useWebRemoveCard() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string, Sender>({
    retry: false,
    mutationFn: (id) => webFetch<{ success: boolean }>(`/profile/payment-methods/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onMutate: () => ({ who: signedInId(queryClient) }),
    onSuccess: async (_r, id, sent) => {
      if (sent?.who !== signedInId(queryClient)) return;
      queryClient.setQueryData<PaymentMethodsPayload>(accountKeys.cards, (cur) => (cur ? { cards: cur.cards.filter((c) => c.id !== id) } : cur));
      await queryClient.invalidateQueries({ queryKey: accountKeys.cards });
    },
  });
}
